import SimpleSpinner from '@components/SimpleSpinner'
import { DewiCard } from '@containers/auction'
import { useSolana } from '@dewicats/connect-button'
import { useAuctionManager } from '@hooks/useAuctionManager'
import { differenceInMinutes, isPast } from 'date-fns'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfoIcon from '@assets/info.svg'
type AuctionActionsProps = {
  currentDewiCat: DewiCard
  currentBid: number
}

const AuctionActions = ({
  currentDewiCat,
  currentBid,
}: AuctionActionsProps) => {
  useAuctionManager()
  const { t } = useTranslation()
  const { anchorProvider } = useSolana()
  const [bidding, setBidding] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [walletHolderBid, setWalletHolderBid] = useState(null)
  const [bidCanceled, setBidCanceled] = useState(false)

  const {
    bidAmount,
    setBidAmount,
    referralCode,
    setReferralCode,
    createBid,
    bids,
    cancelBid,
    highestBidder,
    currentListing,
    currentMinimumBid,
    symbol,
    minBidIncrement,
  } = useAuctionManager()

  const isStakeTypeListing = useMemo(
    () => currentListing?.rewardType?.stake !== undefined,
    [currentListing],
  )

  useEffect(() => {
    const found = bids?.find(
      (bid) =>
        bid.bidder.toBase58() ===
          anchorProvider?.wallet?.publicKey?.toBase58() && bid.state.active,
    )
    if (found) setWalletHolderBid(found)
  }, [anchorProvider?.wallet?.publicKey, bids])

  const onBid = useCallback(async () => {
    if (!anchorProvider?.publicKey) {
      const connectWalletButton = document.querySelector(
        '#connect-wallet-button',
      ) as HTMLButtonElement

      connectWalletButton.click()
      return
    }

    if (bidAmount < currentBid) {
      setError(t('auction.errors.bidLessThanCurrent'))
      return
    }

    if (bidAmount < currentBid + minBidIncrement) {
      setError(
        t('auction.errors.bidTooLow', {
          minimunBidIncrement: minBidIncrement,
        }),
      )

      return
    }

    try {
      setBidding(true) // Start the bidding process
      await createBid(bidAmount, referralCode) // Attempt to create a bid with the specified amount
    } catch (error) {
      console.error('Failed to create bid:', error) // Log the error
    } finally {
      setBidding(false)
    }
  }, [
    anchorProvider?.publicKey,
    bidAmount,
    currentBid,
    t,
    createBid,
    referralCode,
  ])

  const onCancelBid = useCallback(async () => {
    if (!walletHolderBid) return

    try {
      setCanceling(true)
      await cancelBid()
      setBidCanceled(true)
    } catch (e) {
      console.error('Failed to cancel bid:', e)
    } finally {
      setCanceling(false)
    }
    setCanceling(false)
  }, [walletHolderBid, cancelBid])

  const cancelBidDisabled = useMemo(() => {
    if (!highestBidder || !walletHolderBid) return true

    return (
      highestBidder?.bidder?.toBase58() === walletHolderBid?.bidder?.toBase58()
    )
  }, [highestBidder, walletHolderBid])

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setBidAmount(null)
      return
    }

    setBidAmount(+e.target.value)
  }

  const handleReferralInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        setReferralCode('')
        return
      }

      const dewiNumber = e.target.value.replace('DeWiCat #', '')

      if (dewiNumber === '') {
        setReferralCode('')
        setError(null)
        return
      }

      // check if its a number
      if (isNaN(Number(dewiNumber))) {
        setError('Invalid DeWiCat Number')
        return
      }

      // Always include "DeWiCat #" as a prefix
      const referralCode = 'DeWiCat #' + dewiNumber

      setError(null)
      setReferralCode(referralCode)
    },
    [],
  )

  const bidEnded = useMemo(() => {
    return isPast(new Date(currentListing?.endAt.toNumber() * 1000))
  }, [currentListing])

  const hasUncanceledBid = useMemo(() => {
    if (
      highestBidder?.bidder?.toBase58() ===
      anchorProvider?.wallet?.publicKey?.toBase58()
    ) {
      return false
    }

    return bids?.some(
      (bid) =>
        bid.bidder.toBase58() ===
          anchorProvider?.wallet?.publicKey?.toBase58() && bid.state.active,
    )
  }, [highestBidder?.bidder, anchorProvider?.wallet?.publicKey, bids])

  const referralCodeDisabled = useMemo(() => {
    return currentListing.rewardPercentage.toNumber() === 0
  }, [currentListing])

  const listingEnded = useMemo(() => {
    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)
    return isPast(auctionEndDate)
  }, [currentListing])

  const lessThanFiveMinutes = useMemo(() => {
    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)
    return differenceInMinutes(auctionEndDate, new Date()) < 5
  }, [currentListing])

  if (bidEnded && !hasUncanceledBid) {
    return null
  }

  return (
    <div className="p-4">
      {currentDewiCat?.soldPrice ? (
        <>
          {hasUncanceledBid && (
            <button
              onClick={onCancelBid}
              className={`btn font-semibold rounded-md mt-4 xs:w-full lg:w-auto`}
              disabled={cancelBidDisabled || bidCanceled}
              style={{
                minWidth: '114.43px',
              }}
            >
              {canceling ? <SimpleSpinner /> : t('auction.cancelBid')}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="flex gap-6 xs:flex-col md:flex-col xs:items-center md:items-start">
            <div className="flex flex-wrap gap-4 items-end xs:w-full lg:w-auto">
              <div className="flex flex-col gap-1 xs:w-full lg:w-auto">
                <h4 className="ml-1 text-gray-6 text-size-1 font-medium">
                  Bid Amount
                </h4>
                <input
                  value={bidAmount}
                  onFocus={() => setError(null)}
                  onChange={(e) => handleInput(e)}
                  type="number"
                  className={`input input-error xs:w-full lg:w-auto border-[1px] border-dark-4`}
                  style={{
                    borderWidth: error ? '2px !important' : '',
                    borderColor: error ? 'red !important' : '',
                  }}
                  placeholder={t('auction.minimunBid', {
                    bid: currentMinimumBid,
                    symbol,
                  })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onBid()
                    }
                  }}
                />
              </div>

              {!referralCodeDisabled && !isStakeTypeListing && (
                <div className="flex flex-col gap-1 xs:w-full lg:w-auto">
                  <h4 className="ml-1 text-gray-6 text-size-1 font-medium">
                    Referral Code
                  </h4>

                  <input
                    value={referralCode}
                    onFocus={() => setError(null)}
                    onChange={(e) => handleReferralInput(e)}
                    type="text"
                    className="input input-error border-[1px] border-dark-6 xs:w-full lg:w-auto"
                    style={{
                      borderWidth: error ? '2px !important' : '',
                      borderColor: error ? 'red !important' : '',
                    }}
                    placeholder="DeWiCat #"
                  />
                </div>
              )}
              <div className="flex flex-col md:flex-row xs:gap-4 lg:gap-2 xs:pb-4 md:pb-0 xs:w-full md:w-auto">
                <button
                  className="btn font-semibold rounded-md xs:w-full md:w-auto"
                  onClick={onBid}
                  style={{
                    minWidth: '103.125px',
                  }}
                >
                  {bidding ? <SimpleSpinner /> : t('auction.placeBid')}
                </button>
                {!cancelBidDisabled && (
                  <button
                    onClick={onCancelBid}
                    className={`btn font-semibold rounded-md`}
                    disabled={cancelBidDisabled || bidCanceled}
                    style={{
                      minWidth: '114.43px',
                    }}
                  >
                    {canceling ? <SimpleSpinner /> : t('auction.cancelBid')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {!bidEnded && <p className="text-red-500">{error}</p>}

      {!listingEnded && currentDewiCat && currentBid && lessThanFiveMinutes && (
        <div className="flex w-full bg-orange-7 border-[1px] border-orange-5 p-4 rounded-md gap-2 mt-4">
          <InfoIcon color="var(--dewi_colors_dark_9)" opacity={0.75} />
          <span className="text-dark-9 text-xs font-semibold opacity-75">
            Bids within the last 5 minutes will reset the end time to 5 minutes
          </span>
        </div>
      )}

      {!listingEnded &&
        currentDewiCat &&
        currentBid &&
        !lessThanFiveMinutes && (
          <div className="flex bg-blue-7 border-[1px] border-blue-5 p-4 rounded-md gap-2 mt-4">
            <div className="flex flex-row gap-2 text-dark-9 opacity-75 justify-center items-center">
              <InfoIcon
                style={{
                  color: 'inherit',
                }}
              />
              <button
                className="flex-1 flex"
                onClick={() => {
                  // open in new tab
                  window.open(
                    `https://raydium.io/swap/?inputMint=sol&outputMint=${currentListing.tokenMint.toBase58()}`,
                    '_blank',
                  )
                }}
              >
                <span className="text-inherit text-xs font-semibold">
                  {`Need help swapping to today's auction token? Click here!`}
                </span>
              </button>
            </div>
          </div>
        )}
    </div>
  )
}

export default AuctionActions
